import React from "react";
import { Box, Typography } from "@mui/material";
import {
  FilePdfOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { FormattedMessage } from "react-intl";

interface PdfUploaderProps {
  id: string;
  file?: File | string | null;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => void;
  setFieldValue: (field: string, value: any) => void;
  label: string;
  fieldName: string;
  accept?: string;
  maxSize?: number; // in MB
  error?: string;
}

const PdfUploader: React.FC<PdfUploaderProps> = ({
  id,
  file,
  onChange,
  setFieldValue,
  label,
  fieldName,
  accept = ".pdf",
  maxSize = 10,
  error,
}) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > maxSize * 1024 * 1024) {
        alert(`File size must be less than ${maxSize}MB`);
        return;
      }
      if (!selectedFile.type.includes("pdf")) {
        alert("Please select a PDF file");
        return;
      }
    }
    onChange(event, setFieldValue);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFieldValue(fieldName, null);
  };

  const handleViewFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (file) {
      const fileUrl =
        typeof file === "string" ? file : URL.createObjectURL(file);
      window.open(fileUrl, "_blank");
    }
  };

  const handleDownloadFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (file) {
      const fileUrl =
        typeof file === "string" ? file : URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = getFileName() || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFileName = () => {
    if (!file) return null;
    if (typeof file === "string") {
      return file.split("/").pop() || "PDF File";
    }
    return file.name;
  };

  const getFileSize = () => {
    if (!file || typeof file === "string") return "";
    return `(${(file.size / 1024 / 1024).toFixed(2)} MB)`;
  };

  return (
    <Box>
      <Box
        sx={{
          border: "1px dashed #ccc",
          borderRadius: 1,
          p: 1,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <input
          type="file"
          accept={accept}
          style={{ display: "none" }}
          id={id}
          onChange={handleFileChange}
        />
        <label htmlFor={id}>
          {file ? (
            <Box
              sx={{
                height: "75px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flex: 1,
                }}
              >
                <FilePdfOutlined style={{ fontSize: 40, color: "red" }} />
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    noWrap
                    title={getFileName() || ""}
                  >
                    {getFileName()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    PDF File {getFileSize()}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  position: "absolute",
                  top: -2,
                  right: -2,
                }}
              >
                <EyeOutlined
                  onClick={handleViewFile}
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    color: "#1677ff",
                  }}
                />
                <DownloadOutlined
                  onClick={handleDownloadFile}
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    color: "#52c41a",
                  }}
                />
                <DeleteOutlined
                  onClick={handleRemoveFile}
                  style={{
                    fontSize: 18,
                    cursor: "pointer",
                    color: "red",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                height: "75px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                <FormattedMessage id={label} />
              </Typography>
            </Box>
          )}
        </label>
      </Box>
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default PdfUploader;
